import React, { useMemo } from 'react';
import useElementSize from '../hooks/useElementSize.js';

const BoxPlotViewer = React.memo(function BoxPlotViewer({ table, categoryCol, numericCol, searchIndices, colorConfig, isLogScale }) {
  const [containerRef, { width, height }] = useElementSize({ debounceMs: 50 });
  const isDateCol = useMemo(() => /date/i.test(numericCol), [numericCol]);

  const data = useMemo(() => {
    if (!table || !categoryCol || !numericCol) return null;
    const catData = table.getChild(categoryCol);
    const numData = table.getChild(numericCol);
    if (!catData || !numData) return null;

    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);
    
    const freqs = new Map();
    const catCounts = new Map(); // Track true categorical frequency for sorting

    for (let i = 0; i < source.length; i++) {
      const cat = catData.get(source[i]);
      if (cat != null && cat !== '') {
        const catStr = String(cat);
        catCounts.set(catStr, (catCounts.get(catStr) || 0) + 1);

        const rawNum = numData.get(source[i]);
        let num;
        if (isDateCol) {
          num = new Date(rawNum).getTime();
        } else {
          num = typeof rawNum === 'number' ? rawNum : parseFloat(rawNum);
        }
        
           if (num != null && !isNaN(num) && isFinite(num)) {
          if (isLogScale && !isDateCol) {
            if (num > 0) {
              if (!freqs.has(catStr)) freqs.set(catStr, []);
              freqs.get(catStr).push(Math.log10(num));
            }
          } else {
            if (!freqs.has(catStr)) freqs.set(catStr, []);
            freqs.get(catStr).push(num);
          }
        }
      }
    }


    // Limit to 8 most popular categories
    const topCategories = Array.from(freqs.entries())
      .sort((a, b) => (catCounts.get(b[0]) || 0) - (catCounts.get(a[0]) || 0))
      .slice(0, 8);

    if (topCategories.length === 0) return null;

    let globalMin = Infinity;
    let globalMax = -Infinity;

    const boxes = topCategories.map(([cat, vals]) => {
      vals.sort((a, b) => a - b);
      const q1 = vals[Math.floor(vals.length * 0.25)];
      const median = vals[Math.floor(vals.length * 0.5)];
      const q3 = vals[Math.floor(vals.length * 0.75)];
      const iqr = q3 - q1;
      const minLimit = q1 - 1.5 * iqr;
      const maxLimit = q3 + 1.5 * iqr;
      
      const outliers = [];
      let min = q1;
      let max = q3;
      
      for (let v of vals) {
        if (v < minLimit || v > maxLimit) {
          outliers.push(v);
        } else {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }

      if (min < globalMin) globalMin = min;
      if (max > globalMax) globalMax = max;
      for (let o of outliers) {
        if (o < globalMin) globalMin = o;
        if (o > globalMax) globalMax = o;
      }

      // Subsample outliers to avoid DOM explosion for highly skewed data
      let renderOutliers = outliers;
      if (outliers.length > 150) {
         const step = Math.ceil(outliers.length / 150);
         renderOutliers = outliers.filter((_, idx) => idx % step === 0);
      }

      return { cat, q1, median, q3, min, max, outliers: renderOutliers };
    });

    const padding = (globalMax - globalMin) * 0.05 || 1;
    return { boxes, globalMin: globalMin - padding, globalMax: globalMax + padding };
  }, [table, categoryCol, numericCol, searchIndices, isDateCol, isLogScale]);

  if (!data || data.boxes.length === 0) return <div className="flex items-center justify-center h-full text-gray-500 text-xs">No numeric data available</div>;

  const padding = { top: 10, right: 10, bottom: 30, left: isDateCol ? 65 : 45 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  
  if (innerWidth <= 0 || innerHeight <= 0) return <div ref={containerRef} className="w-full h-full" />;

  const scaleY = (val) => innerHeight - ((val - data.globalMin) / (data.globalMax - data.globalMin)) * innerHeight;
  const bandWidth = innerWidth / data.boxes.length;
  const boxWidth = Math.min(bandWidth * 0.6, 50);

  // Calculate dynamic label size and truncation based on available space per category
  const labelFontSize = bandWidth < 40 ? 8 : (bandWidth < 60 ? 9 : 10);
  const charWidth = labelFontSize * 0.65; // Approx pixel width of a bold character
  const maxChars = Math.max(3, Math.floor((bandWidth - 4) / charWidth));

  // Calculate ticks
  const ticks = [];
  for (let i = 0; i <= 5; i++) {
    ticks.push(data.globalMin + (data.globalMax - data.globalMin) * (i / 5));
  }

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden">
      <svg width={width} height={height} className="absolute top-0 left-0">
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {ticks.map((tick, i) => {
            let tickLabel;
            if (isDateCol) {
              const d = new Date(tick);
              tickLabel = !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
            } else if (isLogScale) {
              const raw = Math.pow(10, tick);
              tickLabel = raw < 0.01 || raw >= 10000 ? raw.toExponential(1) : parseFloat(raw.toPrecision(3)).toString();
            } else {
              tickLabel = tick.toLocaleString(undefined, {maximumFractionDigits: 1});
            }
            return (
              <g key={i} transform={`translate(0, ${scaleY(tick)})`}>
                <line x1={0} y1={0} x2={innerWidth} y2={0} stroke="#e5e7eb" strokeDasharray="4,4" />
                <text x={-5} y={3} textAnchor="end" fontSize={9} fill="#9ca3af">{tickLabel}</text>
              </g>
            );
          })}
          <line x1={0} y1={0} x2={0} y2={innerHeight} stroke="#d1d5db" />
          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
          
          {data.boxes.map((box, i) => {
            const cx = i * bandWidth + bandWidth / 2;
            const color = colorConfig?.colorMap?.get(box.cat) || [59, 130, 246, 200];
            const fill = `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.5)`;
            const stroke = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

            return (
              <g key={box.cat}>
                <line x1={cx} y1={scaleY(box.max)} x2={cx} y2={scaleY(box.q3)} stroke={stroke} strokeWidth={1.5} />
                <line x1={cx - boxWidth/4} y1={scaleY(box.max)} x2={cx + boxWidth/4} y2={scaleY(box.max)} stroke={stroke} strokeWidth={1.5} />
                <line x1={cx} y1={scaleY(box.min)} x2={cx} y2={scaleY(box.q1)} stroke={stroke} strokeWidth={1.5} />
                <line x1={cx - boxWidth/4} y1={scaleY(box.min)} x2={cx + boxWidth/4} y2={scaleY(box.min)} stroke={stroke} strokeWidth={1.5} />
                <rect x={cx - boxWidth/2} y={scaleY(box.q3)} width={boxWidth} height={Math.max(1, scaleY(box.q1) - scaleY(box.q3))} fill={fill} stroke={stroke} strokeWidth={1.5} />
                <line x1={cx - boxWidth/2} y1={scaleY(box.median)} x2={cx + boxWidth/2} y2={scaleY(box.median)} stroke={stroke} strokeWidth={2.5} />
                {box.outliers.map((out, j) => (
                  <circle key={j} cx={cx} cy={scaleY(out)} r={2} fill={fill} stroke={stroke} opacity={0.6} />
                ))}
                <text x={cx} y={innerHeight + 15} textAnchor="middle" fontSize={labelFontSize} fill="#4b5563" fontWeight="bold">
                   <title>{box.cat}</title>                  
                  {box.cat.length > maxChars ? box.cat.substring(0, Math.max(1, maxChars - 2)) + '..' : box.cat}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
});

export default BoxPlotViewer;